/**
 * Teleport-pillar flow (HEX-92 / ADR-0004 travel primitives).
 *
 * Stepping onto a `pillar` tile (navigation primitive: passable, `lifecycleState:
 * null`) raises a fast-travel modal listing every OTHER pillar this character
 * already knows. Travel moves `character.position` within the single endless
 * world (ADR-0018 §1) — there is no other destination axis.
 *
 * Known-pillar derivation (no new registry table — DoD): the host intersects
 * `Character.explored` (hexes this character has personally crossed) with the
 * pillar tiles in the world, excludes the pillar currently stood on, and hands
 * the resulting destinations in via `opts.destinations`. The origin pillar (0, 1)
 * is in the spawn cluster, so it lands in `explored` the first time the player
 * crosses it and thereafter always appears in the list.
 *
 * Cost: `distance × PILLAR_TRAVEL_COST_PER_HEX` gold (documented constant). A
 * destination the character can't afford is greyed (disabled), never hidden, so
 * the player can see where they could go once richer. Travelling deducts the
 * cost, moves the character, persists, and hands back `travelled` with the
 * destination so the host snaps the camera (renderer 'snap') + re-seeds vision.
 *
 * DOM-overlay-above-Pixi, reusing the shared interaction-modal chrome (same as
 * TownFlow / ShopFlow).
 */

import type { Character } from '../core/character';
import { distance as hexDistance } from '../core/hex';
import type { Hex } from '../core/hex';
import type { Tile } from '../core/tile';
import type { Persistence } from '../persistence/Persistence';

/**
 * Gold cost per hex of pillar travel. Small documented constant — pillar travel
 * is a convenience, not a gold sink, but a long hop should still cost something
 * so walking stays the default for short trips.
 */
export const PILLAR_TRAVEL_COST_PER_HEX = 2;

/** Gold cost to fast-travel to `dest` from `from` (distance × per-hex constant). */
export function pillarTravelCost(from: Hex, dest: Hex): number {
  return hexDistance(from, dest) * PILLAR_TRAVEL_COST_PER_HEX;
}

/** `travelled` → host snaps the camera + re-seeds vision at the destination;
 * `declined` → host resumes world input unchanged. */
export type PillarOutcome = 'travelled' | 'declined';

export interface PillarFlowDeps {
  readonly persistence: Pick<Persistence, 'saveCharacter'>;
}

export interface PillarFlowStartOptions {
  readonly character: Character;
  readonly tile: Tile;
  /**
   * Other known pillars this character can travel to — `Character.explored` ∩
   * pillar tiles, with the current pillar already excluded. Resolved by the host
   * (it owns the world store); the flow only renders + transacts.
   */
  readonly destinations: readonly Hex[];
  readonly deps: PillarFlowDeps;
  /** `travelled` carries the destination so the host can snap there. */
  readonly onResolved: (outcome: PillarOutcome, destination?: Hex) => void | Promise<void>;
}

export class PillarFlow {
  private modal: HTMLDivElement | null = null;
  private active = false;

  constructor(private readonly container: HTMLElement) {}

  /** Mount the pillar modal if the tile is a pillar. Returns true when mounted
   * (host sets its `activeOverlay` flag to pause world input). */
  start(opts: PillarFlowStartOptions): boolean {
    if (this.active) return false;
    if (opts.tile.type !== 'pillar') return false;
    this.active = true;
    this.modal = this.mountModal(opts);
    return true;
  }

  isActive(): boolean {
    return this.active;
  }

  destroy(): void {
    this.modal?.remove();
    this.modal = null;
    this.active = false;
  }

  private mountModal(opts: PillarFlowStartOptions): HTMLDivElement {
    const root = document.createElement('div');
    root.className = 'hex6-interaction-modal';
    root.dataset.role = 'pillar-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', 'Teleport pillar');

    const panel = document.createElement('div');
    panel.className = 'hex6-interaction-modal__panel';

    const title = document.createElement('h2');
    title.className = 'hex6-interaction-modal__title';
    title.textContent = 'A teleport pillar';
    panel.appendChild(title);

    const character = opts.character;
    const here: Hex = { q: opts.tile.q, r: opts.tile.r };

    const goldRow = document.createElement('div');
    goldRow.className = 'hex6-interaction-modal__row';
    const goldLabel = document.createElement('span');
    goldLabel.textContent = 'Gold';
    const goldValue = document.createElement('span');
    goldValue.dataset.role = 'pillar-gold';
    goldValue.textContent = String(character.gold);
    goldRow.append(goldLabel, goldValue);
    panel.appendChild(goldRow);

    const list = document.createElement('div');
    list.dataset.role = 'pillar-list';
    list.className = 'hex6-interaction-modal__body';

    if (opts.destinations.length === 0) {
      const empty = document.createElement('div');
      empty.dataset.role = 'pillar-empty';
      empty.textContent = 'No other pillars discovered yet. Cross more pillars to link them.';
      list.appendChild(empty);
    } else {
      // Nearest-first so the cheapest hops read at the top.
      const sorted = [...opts.destinations].sort(
        (a, b) => hexDistance(here, a) - hexDistance(here, b),
      );
      for (const dest of sorted) {
        list.appendChild(this.buildRow(opts, here, dest));
      }
    }
    panel.appendChild(list);

    const actions = document.createElement('div');
    actions.className = 'hex6-interaction-modal__actions';

    const close = document.createElement('button');
    close.type = 'button';
    close.dataset.action = 'pillar-close';
    close.className = 'hex6-interaction-modal__button';
    close.textContent = 'Leave';
    close.addEventListener('click', () => {
      void this.decline(opts);
    });
    actions.appendChild(close);
    panel.appendChild(actions);

    root.appendChild(panel);
    this.container.appendChild(root);
    return root;
  }

  /** One destination row: coords + distance + cost, with a travel button greyed
   * when the character can't afford the hop. */
  private buildRow(opts: PillarFlowStartOptions, here: Hex, dest: Hex): HTMLDivElement {
    const character = opts.character;
    const cost = pillarTravelCost(here, dest);
    const dist = hexDistance(here, dest);
    const affordable = character.gold >= cost;

    const row = document.createElement('div');
    row.className = 'hex6-interaction-modal__row';
    row.dataset.role = 'pillar-destination';
    row.dataset.q = String(dest.q);
    row.dataset.r = String(dest.r);
    if (!affordable) row.dataset.affordable = 'false';

    const label = document.createElement('span');
    label.dataset.role = 'pillar-destination-label';
    label.textContent = `(${dest.q}, ${dest.r}) · ${dist} away`;

    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.action = 'pillar-travel';
    button.className = 'hex6-interaction-modal__button hex6-interaction-modal__button--primary';
    button.textContent = `Travel · ${cost}g`;
    button.disabled = !affordable;
    button.addEventListener('click', () => {
      void this.travel(opts, here, dest);
    });

    row.append(label, button);
    return row;
  }

  /** Travel: deduct the cost, move the character, persist, and hand back
   * `travelled` with the destination so the host snaps the camera. */
  private async travel(opts: PillarFlowStartOptions, here: Hex, dest: Hex): Promise<void> {
    if (!this.active) return;
    const character = opts.character;
    const cost = pillarTravelCost(here, dest);
    if (character.gold < cost) return; // belt-and-braces; button is disabled too
    character.gold -= cost;
    character.position = { q: dest.q, r: dest.r };

    this.destroy();
    await opts.deps.persistence.saveCharacter(character);
    await opts.onResolved('travelled', dest);
  }

  /** Leave: free dismissal, no mutation. Host resumes world input unchanged. */
  private async decline(opts: PillarFlowStartOptions): Promise<void> {
    if (!this.active) return;
    this.destroy();
    await opts.onResolved('declined');
  }
}
